import { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { PaperPlaneTilt, ChatCircle, UploadSimple, FileText } from '@phosphor-icons/react';
import { API } from '@/config/api';

export default function RecruiterMessages() {
  const [conversation, setConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState('');
  const [pendingAttachments, setPendingAttachments] = useState([]);
  const [sending, setSending] = useState(false);
  const [uploading, setUploading] = useState(false);
  const scrollRef = useRef(null);

  const fetchConversation = useCallback(async () => {
    try {
      const convRes = await axios.get(`${API}/messages/conversations`);
      const conv = convRes.data?.[0];
      if (!conv) return;
      setConversation(conv);
      const msgRes = await axios.get(`${API}/messages/conversations/${conv.id}/messages`);
      setMessages(msgRes.data);
    } catch {
      // silent
    }
  }, []);

  useEffect(() => {
    fetchConversation();
    const iv = setInterval(fetchConversation, 6000);
    return () => clearInterval(iv);
  }, [fetchConversation]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages]);

  const sendMessage = async () => {
    if (!conversation || (!draft.trim() && pendingAttachments.length === 0)) return;
    setSending(true);
    try {
      const res = await axios.post(`${API}/messages/conversations/${conversation.id}/messages`, {
        body: draft.trim(),
        attachments: pendingAttachments,
      });
      setMessages(prev => [...prev, res.data]);
      setDraft('');
      setPendingAttachments([]);
    } catch {
      toast.error('Failed to send message');
    } finally {
      setSending(false);
    }
  };

  const handleAttach = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await axios.post(`${API}/messages/upload-attachment`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setPendingAttachments(prev => [...prev, res.data]);
    } catch {
      toast.error('Upload failed');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const downloadAttachment = async (a) => {
    try {
      const resp = await axios.get(`${API}/messages/attachments/${a.file_id}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(resp.data);
      const link = document.createElement('a');
      link.href = url;
      link.download = a.filename;
      link.click();
      setTimeout(() => window.URL.revokeObjectURL(url), 1000);
    } catch {
      toast.error('Download failed');
    }
  };

  return (
    <div className="min-h-screen bg-gray-50" data-testid="recruiter-messages-page">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900 tracking-tight" style={{ fontFamily: "'Libre Baskerville', serif" }}>Messages</h1>
          <p className="text-gray-500 text-sm mt-1">Direct chat with Study Alliance support</p>
        </div>

        <div className="bg-white border border-gray-200 rounded-lg flex flex-col h-[70vh]" data-testid="recruiter-chat-pane">
          <div className="px-4 py-3 border-b border-gray-100 flex items-center gap-2">
            <div className="w-9 h-9 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center">
              <ChatCircle size={18} weight="fill" />
            </div>
            <div>
              <p className="text-sm font-semibold text-gray-900">Study Alliance Support</p>
              <p className="text-xs text-gray-500">Admin team</p>
            </div>
          </div>

          <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3 bg-gray-50" data-testid="recruiter-messages-scroll">
            {messages.length === 0 ? (
              <p className="text-sm text-gray-400 text-center py-8">No messages yet. Start a conversation below.</p>
            ) : messages.map(m => (
              <div key={m.id} className={`flex ${m.sender_role === 'recruiter' ? 'justify-end' : 'justify-start'}`} data-testid={`recruiter-message-${m.id}`}>
                <div className={`max-w-[75%] rounded-2xl px-4 py-2 shadow-sm ${m.sender_role === 'recruiter' ? 'bg-blue-700 text-white rounded-br-sm' : 'bg-white border border-gray-200 text-gray-900 rounded-bl-sm'}`}>
                  {m.body && <p className="text-sm whitespace-pre-wrap break-words">{m.body}</p>}
                  {m.attachments?.length > 0 && (
                    <div className="mt-2 space-y-1">
                      {m.attachments.map(a => (
                        <button
                          key={a.file_id}
                          onClick={() => downloadAttachment(a)}
                          className={`flex items-center gap-2 rounded-md px-2 py-1.5 text-xs w-full text-left ${m.sender_role === 'recruiter' ? 'bg-blue-800 hover:bg-blue-900 text-blue-100' : 'bg-gray-100 hover:bg-gray-200 text-gray-700'}`}
                          data-testid={`recruiter-msg-attachment-${a.file_id}`}
                        >
                          <FileText size={14} />
                          <span className="truncate">{a.filename}</span>
                          {a.size > 0 && <span className="opacity-70 ml-auto">{(a.size / 1024).toFixed(1)} KB</span>}
                        </button>
                      ))}
                    </div>
                  )}
                  <p className={`text-[10px] mt-1 ${m.sender_role === 'recruiter' ? 'text-blue-200' : 'text-gray-400'}`}>{new Date(m.created_at).toLocaleString()}</p>
                </div>
              </div>
            ))}
          </div>

          {pendingAttachments.length > 0 && (
            <div className="border-t border-gray-100 px-3 pt-2 flex flex-wrap gap-2" data-testid="recruiter-pending-attachments">
              {pendingAttachments.map(a => (
                <div key={a.file_id} className="flex items-center gap-1.5 bg-gray-100 rounded-md px-2 py-1 text-xs">
                  <FileText size={12} />
                  <span className="truncate max-w-[140px]">{a.filename}</span>
                  <button onClick={() => setPendingAttachments(prev => prev.filter(x => x.file_id !== a.file_id))} className="text-gray-500 hover:text-red-600 ml-1">✕</button>
                </div>
              ))}
            </div>
          )}

          <div className="border-t border-gray-100 p-3 flex items-end gap-2">
            <Label htmlFor="recruiter-msg-attach" className="cursor-pointer shrink-0" data-testid="recruiter-attach-button">
              <div className={`h-10 w-10 flex items-center justify-center rounded-md border border-gray-200 hover:bg-gray-50 ${uploading ? 'opacity-50' : ''}`}>
                <UploadSimple size={18} className="text-gray-600" />
              </div>
            </Label>
            <input id="recruiter-msg-attach" type="file" className="hidden" onChange={handleAttach} disabled={uploading} data-testid="recruiter-attach-input" />
            <Textarea
              value={draft}
              onChange={e => setDraft(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(); } }}
              placeholder="Write a message… (Enter to send, Shift+Enter for new line)"
              rows={2}
              className="flex-1 resize-none text-sm"
              data-testid="recruiter-message-input"
            />
            <Button onClick={sendMessage} disabled={sending || (!draft.trim() && pendingAttachments.length === 0)} className="bg-blue-700 hover:bg-blue-800 text-white" data-testid="recruiter-send-message-btn"><PaperPlaneTilt size={14} className="mr-1" /> Send</Button>
          </div>
        </div>
      </div>
    </div>
  );
}
